import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { BaseStatusBar } from '..';
import { getStatusesStringList } from '../../helpers/common.helpers';
import { RequestContext } from '../../context/Request.context';

import { AMENDMENT_TYPE, STATUS } from '../../constants/status.constant';
import AppConfig from '../../providers/AppConfig';

import './FormWrapper.scss';

// Map amendment types to translation keys
const getAmendmentTranslationKey = (type) => {
  const typeMap = {
    [AMENDMENT_TYPE.DEFAULT]: 'amendmentStatus',
    [AMENDMENT_TYPE.RENEWAL]: 'renewalStatus',
    [AMENDMENT_TYPE.EMPLOYER_CHANGE]: 'employerChangeStatus',
    [AMENDMENT_TYPE.DELETION]: 'deletion',
  };
  return typeMap[type] || 'amendmentStatus';
};

export const FormWrapper = ({ children }) => {
  const { t } = useTranslation();
  const { request, resources } = useContext(RequestContext);

  const status = resources?.status;
  const statuses = useMemo(() => (status?.data ? getStatusesStringList(status.data) : []), [status]);

  if (!status)
    return (
      <div className="form-wrapper">
        <div className="form-wrapper__content">{children}</div>
      </div>
    );

  const requestStatus = request.status;

  const amendment = request.amendment;
  const amendmentStatus = amendment?.status;
  const amendmentType = amendment?.type;

  const amendmentTranslationKey = amendmentType != null ? getAmendmentTranslationKey(amendmentType) : null;

  const amendmentDescription =
    amendment?.rejected_comment ||
    (amendmentTranslationKey && amendmentStatus
      ? t(`request.form.${amendmentTranslationKey}.${statuses[amendmentStatus]}.description`)
      : '');

  const deletionDate = request.deletion_date;
  const isDeletionPendingApproval = amendmentType === AMENDMENT_TYPE.DELETION && amendmentStatus === STATUS.IN_REVIEW;
  const isDeletionApproved = !isDeletionPendingApproval && !!deletionDate;
  const formattedDeletionDate = deletionDate ? dayjs(deletionDate).format(AppConfig.dotsDateFormat) : null;

  const renderStatusBar = () => {
    switch (true) {
      case isDeletionPendingApproval:
        return (
          <BaseStatusBar
            title={t('request.form.deletion.inProgress.title')}
            description={t('request.form.deletion.inProgress.description')}
            statusText={statuses[amendmentStatus]}
            status={amendmentStatus}
          />
        );
      case isDeletionApproved:
        return (
          <BaseStatusBar
            title={t('request.form.deletion.approved.title')}
            description={t('request.form.deletion.approved.description', { date: formattedDeletionDate })}
            statusText="deletion"
            status={amendmentStatus}
          />
        );
      default:
        return (
          <>
            {!amendment &&
              AppConfig.detailsStatuses.includes(requestStatus) &&
              (amendmentType !== AMENDMENT_TYPE.RENEWAL || !AppConfig.deleteStatuses.includes(amendmentStatus)) && (
                <BaseStatusBar
                  title={t(`request.form.status.${statuses[requestStatus]}.title`)}
                  description={t(`request.form.status.${statuses[requestStatus]}.description`)}
                  statusText={statuses[requestStatus]}
                  status={requestStatus}
                  registrationOfficerComment={request.registration_officer_comment || ''}
                />
              )}
            {amendment &&
              amendmentType !== AMENDMENT_TYPE.DELETION &&
              AppConfig.detailsStatuses.includes(amendmentStatus) && (
                <BaseStatusBar
                  title={t(`request.form.${amendmentTranslationKey}.${statuses[amendmentStatus]}.title`)}
                  description={amendmentDescription}
                  statusText={statuses[amendmentStatus]}
                  status={amendmentStatus}
                />
              )}
          </>
        );
    }
  };

  return (
    <div className="form-wrapper">
      {renderStatusBar()}
      <div className="form-wrapper__content">
        {React.Children.map(children, (child) => {
          if (!child) return null;
          return React.cloneElement(child, {
            isDeletionProcessStarted: isDeletionPendingApproval || isDeletionApproved,
          });
        })}
      </div>
    </div>
  );
};

export default FormWrapper;
